<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore'
import { db } from '@/firebase'
import { useOpsStore } from '@/stores/ops'
import { useHazardsStore } from '@/stores/hazards'
import { useIAPStore } from '@/stores/iap'
import { useAuthStore } from '@/stores/auth'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import type { AgencyHazardTemplate } from '@/types'

const props = defineProps<{ incidentId: string }>()
defineEmits<{ prev: [] }>()

const opsStore = useOpsStore()
const hazardsStore = useHazardsStore()
const iapStore = useIAPStore()
const authStore = useAuthStore()

const opId = computed(() => opsStore.selectedOpId ?? '')
const agencyId = computed(() => authStore.userProfile?.agency_id ?? '')

const templates = ref<AgencyHazardTemplate[]>([])
const showTemplateDialog = ref(false)

onMounted(() => {
  if (!agencyId.value) return
  const q = query(
    collection(db, 'agencies', agencyId.value, 'hazard_templates'),
    orderBy('order'),
  )
  onSnapshot(q, (snap) => {
    templates.value = snap.docs.map((d) => ({ template_id: d.id, ...d.data() }) as AgencyHazardTemplate)
  })
})

async function addFromTemplate(t: AgencyHazardTemplate) {
  if (!opId.value) return
  await hazardsStore.addHazard(props.incidentId, opId.value, t.hazard, t.mitigation)
  showTemplateDialog.value = false
}

async function addCustom() {
  if (!opId.value) return
  await hazardsStore.addHazard(props.incidentId, opId.value, '', '')
}

async function updateField(hazardId: string, field: 'hazard' | 'mitigation', value: string) {
  if (!opId.value) return
  await hazardsStore.updateHazard(props.incidentId, opId.value, hazardId, { [field]: value })
}

async function removeHazard(hazardId: string) {
  if (!opId.value) return
  await hazardsStore.removeHazard(props.incidentId, opId.value, hazardId)
}

const op = computed(() => opsStore.selectedOp)

async function saveSafetyMessage(value: string) {
  if (!opId.value) return
  await opsStore.updateOp(props.incidentId, opId.value, { safety_message: value })
}

const preparedBy208 = computed(() => iapStore.iap?.prepared_by_208 ?? '')
async function savePreparedBy(value: string) {
  if (!opId.value) return
  await iapStore.savePreparedBy(props.incidentId, opId.value, '208', value)
}
</script>

<template>
  <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
    <!-- Section head -->
    <div class="flex items-end gap-3.5 flex-wrap">
      <div class="flex flex-col gap-0.5">
        <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 208 · Required</span>
        <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Safety Message &amp; Plan</h2>
      </div>
      <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Identified hazards, mitigations, and safety message for this operational period.</p>
    </div>

    <!-- Hazards group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Hazards</h3>
        <div class="ml-auto flex gap-1.5">
          <button class="inline-flex items-center gap-1.5 bg-surface border border-border rounded px-2.5 py-1.5 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-on-surface hover:bg-surface-container-low transition-colors" @click="showTemplateDialog = true">
            <span class="material-symbols-outlined text-[14px]">library_add</span>
            Templates
          </button>
          <button class="inline-flex items-center gap-1.5 bg-surface border border-dashed border-border-strong rounded px-2.5 py-1.5 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary hover:border-primary transition-colors" @click="addCustom">
            <span class="material-symbols-outlined text-[14px]">add</span>
            Custom Hazard
          </button>
        </div>
      </div>

      <div class="flex flex-col">
        <div v-for="(hazard, idx) in hazardsStore.hazards" :key="hazard.hazard_id" class="grid gap-2.5 px-3.5 py-2.5 border-b border-border/60 last:border-0 group" style="grid-template-columns: 22px 1fr 24px;">
          <span class="font-mono font-semibold text-[12px] text-outline text-center pt-2">{{ idx + 1 }}</span>
          <div class="flex flex-col gap-1.5">
            <input
              :value="hazard.hazard"
              placeholder="Hazard name…"
              class="font-headline font-bold text-[14px] text-on-surface bg-transparent border border-transparent rounded px-2 py-1 hover:border-border/60 hover:bg-white focus:border-primary focus:bg-white focus:shadow-[0_0_0_3px_rgba(23,80,158,0.15)] focus:outline-none w-full box-border"
              @blur="updateField(hazard.hazard_id, 'hazard', ($event.target as HTMLInputElement).value)"
            />
            <div>
              <p class="font-mono text-[9px] tracking-[0.12em] uppercase text-outline mb-1">Mitigation</p>
              <textarea
                :value="hazard.mitigation"
                placeholder="Describe the mitigation measure…"
                rows="2"
                class="font-body text-[12px] text-on-surface bg-transparent border border-transparent rounded px-2 py-1 hover:border-border/60 hover:bg-white focus:border-primary focus:bg-white focus:shadow-[0_0_0_3px_rgba(23,80,158,0.15)] focus:outline-none w-full box-border resize-vertical min-h-[36px] leading-relaxed"
                @blur="updateField(hazard.hazard_id, 'mitigation', ($event.target as HTMLTextAreaElement).value)"
              />
            </div>
          </div>
          <button class="w-6 h-6 inline-flex items-center justify-center text-outline hover:text-error border border-transparent rounded hover:border-border hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity mt-1.5 self-start" @click="removeHazard(hazard.hazard_id)">
            <span class="material-symbols-outlined text-[16px]">close</span>
          </button>
        </div>
        <div v-if="hazardsStore.hazards.length === 0" class="font-body text-[13px] text-outline py-6 text-center">
          No hazards added. Use "Templates" or "Custom Hazard" above.
        </div>
      </div>
    </div>

    <!-- Caution stripe divider -->
    <div class="h-2" style="background-image: repeating-linear-gradient(-45deg, rgba(26,26,24,0.08) 0, rgba(26,26,24,0.08) 4px, transparent 4px, transparent 8px);" />

    <!-- General Safety Message group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">General Safety Message</h3>
      </div>
      <div class="p-3.5">
        <FosTextarea :model-value="op?.safety_message ?? ''" placeholder="General safety message for this OP…" :rows="3" @blur="saveSafetyMessage(($event.target as HTMLTextAreaElement).value)" />
      </div>
    </div>

    <!-- Prepared By group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Prepared By</h3>
      </div>
      <div class="p-3.5">
        <FosInput :model-value="preparedBy208" placeholder="Name of person preparing ICS 208…" @blur="savePreparedBy(($event.target as HTMLInputElement).value)" />
      </div>
    </div>

    <!-- Section footer (last section — no Next) -->
    <div class="flex items-center gap-2.5 mt-2 pt-3 border-t border-border/60">
      <button class="inline-flex items-center gap-1.5 bg-surface border border-border rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-on-surface cursor-pointer hover:bg-surface-container-low transition-colors" @click="$emit('prev')">
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        ICS 207
      </button>
      <span class="font-mono text-[10px] tracking-[0.08em] uppercase text-outline ml-auto">Last section</span>
    </div>

    <!-- Templates dialog -->
    <FosDialog :open="showTemplateDialog" title="Hazard Templates" @update:open="showTemplateDialog = $event">
      <div class="space-y-2 min-h-[100px]">
        <div v-for="t in templates" :key="t.template_id" class="flex items-start justify-between gap-3 p-3 border border-border rounded hover:bg-surface-container-high cursor-pointer" @click="addFromTemplate(t)">
          <div class="min-w-0">
            <p class="text-sm font-semibold text-on-surface">{{ t.hazard }}</p>
            <p class="text-xs text-outline mt-0.5 truncate">{{ t.mitigation }}</p>
          </div>
          <span class="material-symbols-outlined text-[18px] text-outline shrink-0">add_circle</span>
        </div>
        <p v-if="templates.length === 0" class="text-sm text-outline py-4 text-center">No hazard templates configured for your agency.</p>
      </div>
    </FosDialog>
  </div>
</template>
